import { useQuery } from '@tanstack/react-query';
import { useOkapiKy } from '@folio/stripes/core';

const PER_PAGE = 30;

/**
 * Build CQL query from URL search params.
 * Reads filters, query, sort directly from location.search — works on refresh.
 */
export function buildCQL(searchParams) {
  const parts = [];

  const q = searchParams.get('query')?.trim();
  if (q) parts.push(`(name="${q}*" or desc="${q}*" or builderType="${q}*")`);

  const filters = searchParams.get('filters') || '';
  if (filters) {
    const filterMap = {};
    filters.split(',').forEach(f => {
      const dot = f.indexOf('.');
      if (dot === -1) return;
      const name = f.substring(0, dot);
      const value = f.substring(dot + 1);
      if (!filterMap[name]) filterMap[name] = [];
      filterMap[name].push(value);
    });

    const cqlMap = {
      status:      'status',
      builderType: 'builderType',
      visibility:  'permissions.visibility',
      complexity:  'performance.complexity',
      categoryId:  'categoryId',
      reportType:  'reportType',
    };

    Object.entries(filterMap).forEach(([name, values]) => {
      const field = cqlMap[name];
      if (!field || !values.length) return;
      const or = values.map(v => `${field}=="${v}"`).join(' or ');
      parts.push(values.length > 1 ? `(${or})` : or);
    });
  }

  const sort = searchParams.get('sort') || 'name';
  const sortDir = sort.startsWith('-') ? '/sort.descending' : '/sort.ascending';
  const cql = parts.length ? parts.join(' and ') : 'cql.allRecords=1';
  return `${cql} sortby ${sort.replace(/^-/, '')}${sortDir}`;
}

export default function useReportsAPI({ searchParams, offset = 0 }) {
  const okapiKy = useOkapiKy();
  const cql = buildCQL(searchParams);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['reports', cql, offset],
    queryFn: () =>
      okapiKy.get(`reports?limit=${PER_PAGE}&offset=${offset}&query=${encodeURIComponent(cql)}`).json(),
    staleTime: 2 * 60 * 1000,
    keepPreviousData: true,
    retry: 1,
  });

  return {
    reports:      data?.reports || [],
    totalRecords: data?.totalRecords || 0,
    isLoading,
    isFetching,
    cql,
  };
}
